import {
  SubmissionStatus,
  type InputProps,
  type LeadSubmissionProps,
  type PiiProps,
  type PopupExperienceProps,
  type ResultsPageProps,
  type ResultsProps,
  type SingleSubmissionSlideoutProps,
  type AdditionalQuestionsProps,
  type SubmissionsProps,
  type SubmittedProgram,
} from './types';
import {
  useCallback,
  useState,
  type FC,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import type { LeadSubmitResponse } from '@highereducation/delivery-system';
import { useLeadSubmissionWithQuestions } from '../../hooks/useLeadSubmissionWithQuestions';
import { mergePartnerIdentifiers } from '../../utils/mergePartnerIdentifiers';
import useRecommendationPopup from '../../hooks/useRecommendationPopup';
import useSingleSubmissionSlideout from '../../hooks/useSingleSubmissionSlideout';
import type { Result } from '../../types/result';
import useActiveProspectScript from '../../hooks/useActiveProspectScript';
import useJornayaScript from '../../hooks/useJornayaScript';
import DuplicateSubmissionPopup from '../DuplicateSubmissionPopup';
import useDuplicatePrograms from '../../hooks/useDuplicatePrograms';
import { getSubmissionErrorStatus } from '../../utils/getSubmissionErrorStatus';
import {
  getCurrentFieldValues,
  getDataByFieldFromFlow,
  transformFlowStateToLeadInputs,
} from '../../utils/dataFromFlowUtils';
import useFlowTrackingEvents from '../../hooks/useFlowTrackingEvents';
import { useResultsPageConfigOptional } from '../../providers/ResultsPageConfigProvider';
import buildLeadObject from '../../utils/buildLeadObject';
import type Lead from '../../types/leadDelivery';
import { useDynamicExperienceContext } from '../../providers/DynamicExperienceProvider';
import getQualifiedResults from '../../services/qualifiedResults';
import type { LocationInference } from '../../utils/data-fetch/fetchQualifiedResults';
import { EventingLocation } from '../../types/enums';
import { useSiteConfig } from '../../providers/ExperienceConfigProvider';
import { uuid } from '../../shared/utils';

import ResultsPageUi from './variants/Default';

type Props = {
  // `variant` is accepted (and ignored) on the public surface so existing
  // published DSL `StepResult` payloads keep validating. The page now renders
  // a single unified UI regardless of the value.
  variant?: string;
  linkoutConfig?: { schoolIds: number[] };
  title?: string;
  subtitle?: string;
};

function getLinkout(
  result: Result,
  linkoutSchoolIds: number[] | undefined,
): string | null {
  if (!linkoutSchoolIds) {
    return null;
  }
  const parsedSchoolId = parseInt(result.schoolId);
  if (!linkoutSchoolIds.includes(parsedSchoolId) || !result.linkoutUrl) {
    return null;
  }
  const correlationId = result.productClickedCorrelationId || uuid();
  const trackingContext = JSON.stringify({
    webContext: (window as any)?._Cohesion?.webContext,
    formatSubtype: 'dexp-results',
    formatType: 'dexp-offering',
  });
  const url =
    result.linkoutUrl +
    (result.linkoutUrl.includes('?') ? '&' : '?') +
    `correlationId=${encodeURIComponent(correlationId)}&trackingContext=${encodeURIComponent(trackingContext)}`;
  return url;
}

const ResultsPage: FC<Props> = ({ linkoutConfig, title, subtitle }: Props) => {
  const siteConfig = useSiteConfig();
  const dynamicExperienceContext = useDynamicExperienceContext();
  const { triggerElementLoadedEvent, triggerElementViewedEvent } =
    useFlowTrackingEvents();
  const flowAnswers = getCurrentFieldValues(
    dynamicExperienceContext.steps,
    dynamicExperienceContext.flowState,
  );

  // DCS Values
  const degree = getDataByFieldFromFlow(
    'degree',
    dynamicExperienceContext.flowState,
  );
  const category = getDataByFieldFromFlow(
    'category',
    dynamicExperienceContext.flowState,
  );
  const subject = getDataByFieldFromFlow(
    'subject',
    dynamicExperienceContext.flowState,
  );

  // Build inputs from flow state — same approach as buildContactData in the original
  const flowInputs = transformFlowStateToLeadInputs(
    dynamicExperienceContext.flowState,
  );

  // PII Values from flow state inputs (same source as buildContactData)
  const firstName = flowInputs.firstName?.value?.toString() || '';
  const lastName = flowInputs.lastName?.value?.toString() || '';
  const email = flowInputs.email?.value?.toString() || '';
  const phone = flowInputs.phoneNumber?.value?.toString() || '';

  // Results state
  const [selectedResult, setSelectedResult] = useState<Result>();
  const selectedResultRef = useRef(selectedResult);
  selectedResultRef.current = selectedResult;
  const [exactResults, setExactResults] = useState<Result[]>([]);
  const [relatedResults, setRelatedResults] = useState<Result[]>([]);
  const [recommendedResults, setRecommendedResults] = useState<Result[]>([]);
  const [spotlightResults, setSpotlightResults] = useState<Result[]>([]);
  const [resultsAreLoading, setResultsAreLoading] = useState<boolean>(true);
  const [qualifiedIds, setQualifiedIds] = useState<string[]>([]);
  const [unqualifiedIds, setUnqualifiedIds] = useState<string[]>([]);
  const [locationInference, setLocationInference] =
    useState<LocationInference>();

  // PII form state (initialized from flow state, editable on results page)
  const [piiFirstName, setPiiFirstName] = useState<string>(firstName);
  const [piiLastName, setPiiLastName] = useState<string>(lastName);
  const [piiEmail, setPiiEmail] = useState<string>(email);
  const [piiPhone, setPiiPhone] = useState<string>(phone);
  const [editMode, setEditMode] = useState<boolean>(false);

  const inputs = {
    // Location inference defaults (from Mobius API) — overridden by flow state if present
    ...(locationInference?.stateAbbr
      ? {
          state: {
            key: 'state' as const,
            value: locationInference.stateAbbr,
          },
        }
      : {}),
    ...flowInputs,
    // Default country — only applies if flowInputs doesn't provide one
    ...(!flowInputs.country
      ? {
          country: {
            key: 'country' as const,
            value: locationInference?.countryCode || 'US',
          },
        }
      : {}),
    // PII overrides from form state (for when user edits on results page)
    firstName: { key: 'firstName' as const, value: piiFirstName },
    lastName: { key: 'lastName' as const, value: piiLastName },
    email: { key: 'email' as const, value: piiEmail },
    phoneNumber: { key: 'phoneNumber' as const, value: piiPhone },
  };

  const lead = buildLeadObject(inputs) as Lead;
  const leadRef = useRef<Lead>(lead);
  useEffect(() => {
    leadRef.current = lead;
  }, [lead]);

  const inputtedNoPii = !piiFirstName || !piiLastName || !piiEmail || !piiPhone;

  useEffect(() => {
    const fetchResults = async () => {
      setResultsAreLoading(true);
      try {
        const qualifiedResults = await getQualifiedResults({
          lead,
          publisherToken: siteConfig?.identityToken,
          trackingContext: {
            anonymousId: (window as any)?._Cohesion?.anonymousId,
          },
          degree,
          category,
          subject,
          presentationUrl: window.location.href,
          forceLinkoutSchoolIds:
            linkoutConfig && linkoutConfig.schoolIds
              ? linkoutConfig.schoolIds
              : undefined,
        });
        setExactResults(
          qualifiedResults.exactResults.map((r) => ({
            ...r,
            isUserRequested: true,
          })),
        );
        setRecommendedResults(
          qualifiedResults.recommendedResults.map((r) => ({
            ...r,
            isUserRequested: true,
          })),
        );
        setRelatedResults(
          qualifiedResults.relatedResults.map((r) => ({
            ...r,
            isUserRequested: false,
          })),
        );
        setSpotlightResults(
          qualifiedResults.spotlightResults.map((r) => ({
            ...r,
            isUserRequested: true,
          })),
        );
        setQualifiedIds(qualifiedResults.qualified);
        setUnqualifiedIds(qualifiedResults.unqualified);
        if (qualifiedResults.locationInference) {
          setLocationInference(qualifiedResults.locationInference);
        }
      } catch (error) {
        console.error('Error fetching qualified results:', error);
      } finally {
        setResultsAreLoading(false);
      }
    };

    fetchResults();
  }, []);

  const [submittedPrograms, setSubmittedPrograms] = useState<
    SubmittedProgram[]
  >([]);
  const [showDuplicatePopup, setShowDuplicatePopup] = useState(false);
  const [duplicateResult, setDuplicateResult] = useState<Result | null>(null);
  const { duplicatePrograms, duplicateProgramsLoading } = useDuplicatePrograms({
    duplicateResult,
    leadRef,
    degree,
    category,
    forceLinkoutSchoolIds: linkoutConfig?.schoolIds,
    qualifiedIds,
    unqualifiedIds,
  });

  // Lead submission state
  const [leadSubmissionStatus, setLeadSubmissionStatus] = useState<
    SubmissionStatus | undefined
  >(undefined);
  const [submittedResults, setSubmittedResults] = useState<
    { programId?: number; schoolId?: string }[]
  >([]);
  // Generic partner identifiers (`referenceId` + `partnerIdentifiers`) read
  // off the most recent LDS v4 submit response (EDUN-14483). A single
  // latest-submission value covers this imperative page's single
  // in-flight selection at a time (`selectedResult`); threaded into the
  // Recommender modal and post-submit Card "Next Steps" CTA sources so
  // `applicationCta` fieldMap entries can resolve.
  const [partnerIdentifiers, setPartnerIdentifiers] = useState<
    Record<string, string> | undefined
  >(undefined);
  const handleLeadSubmitted = useCallback(
    (response: LeadSubmitResponse, submittedResult?: Result) => {
      if (submittedResult && selectedResultRef.current !== submittedResult) {
        return;
      }
      setPartnerIdentifiers(mergePartnerIdentifiers(response));
    },
    [],
  );

  const selectResult = useCallback(
    (result: Result) => {
      setSelectedResult(result);
      const hasSubmitted = submittedResults.some(
        (submission) => submission.programId === result.programId,
      );
      setLeadSubmissionStatus(
        hasSubmitted ? SubmissionStatus.SUBMITTED : undefined,
      );
      // Clear identifiers from a prior submission so a not-yet-submitted
      // selection never interpolates a stale referenceId/partnerIdentifiers
      // (EDUN-14483); handleLeadSubmitted repopulates this on this
      // selection's own submit.
      if (!hasSubmitted) {
        setPartnerIdentifiers(undefined);
      }
    },
    [submittedResults],
  );

  /**
   * selectResult marks already-submitted programs as SUBMITTED, which causes
   * SingleProgramSubmission to show the SubmissionStatus overlay. When the
   * user is intentionally navigating to the exit-strategy step (e.g. "View
   * next steps"), we need to clear that status so the slideout renders the
   * correct content.
   */
  const resetSubmissionStatusForExitStrategy = useCallback(() => {
    setLeadSubmissionStatus(undefined);
  }, []);

  const canSubmitLead = useMemo(() => {
    const isSubmittingLead =
      leadSubmissionStatus === SubmissionStatus.SUBMITTING;
    return !isSubmittingLead && !!selectedResult && !inputtedNoPii;
  }, [
    leadSubmissionStatus,
    selectedResult,
    piiFirstName,
    piiLastName,
    piiEmail,
    piiPhone,
  ]);

  const productClickedCorrelationId =
    selectedResult?.productClickedCorrelationId || uuid();

  const record = useMemo(() => {
    if (!selectedResult) return undefined;
    return {
      school: {
        id: selectedResult.schoolId,
        slug: selectedResult.schoolSlug,
        name: selectedResult.schoolName,
      },
      program: {
        id: selectedResult.programId,
        leadSource: flowAnswers.leadSource,
      },
    };
  }, [selectedResult, flowAnswers]);

  const shouldUseActiveProspectToken =
    selectedResult?.showActiveProspectScript === true;

  // Popup experience
  const {
    showPopupExperience,
    closePopupExperience,
    showSpotlight,
    showRecommender,
    popupExperienceStep,
    gotToPopupPiiConfirmation,
    goToPopupAdditionalQuestions,
    goToPopupExitStrategy,
    goToPopupProgramSelect,
    goToPopupSubmission,
  } = useRecommendationPopup({
    recommendedResults,
    spotlightResults,
    productClickedCorrelationId,
  });

  const onPopupProgramClick = useCallback(
    (result: Result) => {
      selectResult(result);
      const linkoutUrl = getLinkout(result, linkoutConfig?.schoolIds);
      if (linkoutUrl) {
        window.open(linkoutUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      gotToPopupPiiConfirmation();
    },
    [gotToPopupPiiConfirmation, selectResult],
  );

  const trackSubmittedResult = useCallback((result?: Result) => {
    if (!result) return;
    setSubmittedResults((prev) => {
      if (prev.some((s) => s.programId === result.programId)) return prev;
      return [
        ...prev,
        { programId: result.programId, schoolId: result.schoolId },
      ];
    });
    setSubmittedPrograms((prev) => {
      if (prev.some((s) => s.program.id === result.programId)) return prev;
      return [
        ...prev,
        {
          program: { id: result.programId, name: result.programName },
          school: { name: result.schoolName, slug: result.schoolSlug },
          applicationCtaLabel: result.applicationCtaLabel,
          applicationCtaUrl: result.applicationCtaURL,
        },
      ];
    });
  }, []);

  // Slideout experience
  const {
    showSlideoutExperience,
    closeSlideoutExperience: originalCloseSlideoutExperience,
    openSlideoutExperience,
    resetSlideoutExperience,
    singleSubmissionExperienceStep,
    goToSlideoutPiiConfirmation,
    goToSlideoutAdditionalQuestions,
    goToSlideoutExitStrategy,
  } = useSingleSubmissionSlideout({
    productClickedCorrelationId,
  });

  // Stay enabled for the page lifetime. The Jornaya hook waits until the
  // TCPA disclosure node is in the document before inserting its script.
  const activeProspectToken = useActiveProspectScript(true);
  const jornayaToken = useJornayaScript(true);

  const {
    showAdditionalQuestions,
    additionalQuestions,
    isLoadingQuestions,
    checkForAdditionalQuestionsAndSubmit,
    submitWithAdditionalData,
  } = useLeadSubmissionWithQuestions({
    lead,
    needsActiveProspectToken: shouldUseActiveProspectToken,
    activeProspectToken: activeProspectToken || '',
    jornayaToken: jornayaToken,
    programId: selectedResult?.programId.toString() || '',
    selectedResult,
    productClickedCorrelationId: productClickedCorrelationId,
    inputs,
    record,
    applicationCtaURL: selectedResult?.applicationCtaURL || '',
    onLeadSubmitted: handleLeadSubmitted,
  });

  const resultsPageConfig = useResultsPageConfigOptional();
  const shouldShowThankYou = resultsPageConfig?.shouldShowThankYou !== false;

  const onPopupProgramSubmit = useCallback(async () => {
    const submittedSelection = selectedResult;
    try {
      setLeadSubmissionStatus(SubmissionStatus.SUBMITTING);
      const { hasQuestions } = await checkForAdditionalQuestionsAndSubmit();
      if (hasQuestions) {
        if (selectedResultRef.current !== submittedSelection) return;
        setLeadSubmissionStatus(SubmissionStatus.PENDING);
        goToPopupAdditionalQuestions();
        return;
      }
      trackSubmittedResult(submittedSelection);
      if (selectedResultRef.current !== submittedSelection) return;
      setLeadSubmissionStatus(SubmissionStatus.SUBMITTING);
      goToPopupSubmission();
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (selectedResultRef.current !== submittedSelection) return;
      setLeadSubmissionStatus(SubmissionStatus.SUBMITTED);
      await new Promise((resolve) => setTimeout(resolve, 1500));
      if (selectedResultRef.current !== submittedSelection) return;
      setLeadSubmissionStatus(undefined);
      if (shouldShowThankYou) {
        goToPopupExitStrategy();
      } else {
        goToPopupProgramSelect();
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      if (selectedResultRef.current !== submittedSelection) return;
      setLeadSubmissionStatus(getSubmissionErrorStatus(error));
    }
  }, [
    selectedResult,
    checkForAdditionalQuestionsAndSubmit,
    goToPopupAdditionalQuestions,
    goToPopupExitStrategy,
    goToPopupProgramSelect,
    goToPopupSubmission,
    trackSubmittedResult,
    shouldShowThankYou,
  ]);

  const closeSlideoutExperience = useCallback(() => {
    originalCloseSlideoutExperience();
  }, [originalCloseSlideoutExperience]);

  const openProgramSlideoutExperience = useCallback(
    (result: Result, skipDuplicateCheck = false) => {
      const isProgramSubmitted = submittedResults.some(
        (submission) => submission.programId === result.programId,
      );

      // Already submitted -- skip the PII form entirely and land the user
      // on the exit-strategy step. Previously the comparison-modal entry
      // point had its own branch; both paths are now identical because
      // the PII step no longer special-cases comparison-modal traffic.
      if (isProgramSubmitted) {
        if (!shouldShowThankYou) return;
        selectResult(result);
        resetSubmissionStatusForExitStrategy();
        openSlideoutExperience();
        goToSlideoutExitStrategy();
        return;
      }

      if (!skipDuplicateCheck) {
        const hasSchoolSubmitted = submittedResults.some(
          (submission) => submission.schoolId === result.schoolId,
        );
        if (hasSchoolSubmitted) {
          setDuplicateResult(result);
          setShowDuplicatePopup(true);
          return;
        }
      }

      selectResult(result);
      const linkoutUrl = getLinkout(result, linkoutConfig?.schoolIds);
      if (linkoutUrl) {
        window.open(linkoutUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      goToSlideoutPiiConfirmation();
      openSlideoutExperience();
    },
    [
      openSlideoutExperience,
      selectResult,
      resetSubmissionStatusForExitStrategy,
      submittedResults,
      goToSlideoutPiiConfirmation,
      goToSlideoutExitStrategy,
      shouldShowThankYou,
    ],
  );

  const onSlideoutProgramSubmit = useCallback(async () => {
    const submittedSelection = selectedResult;
    try {
      setLeadSubmissionStatus(SubmissionStatus.SUBMITTING);

      const { hasQuestions } = await checkForAdditionalQuestionsAndSubmit();
      if (hasQuestions) {
        if (selectedResultRef.current !== submittedSelection) return;
        setLeadSubmissionStatus(SubmissionStatus.PENDING);
        goToSlideoutAdditionalQuestions();
        return;
      }
      trackSubmittedResult(submittedSelection);
      if (selectedResultRef.current !== submittedSelection) return;
      setLeadSubmissionStatus(SubmissionStatus.SUBMITTING);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (selectedResultRef.current !== submittedSelection) return;
      setLeadSubmissionStatus(SubmissionStatus.SUBMITTED);
      await new Promise((resolve) => setTimeout(resolve, 1500));
      if (selectedResultRef.current !== submittedSelection) return;
      setLeadSubmissionStatus(undefined);
      if (shouldShowThankYou) {
        goToSlideoutExitStrategy();
      } else {
        resetSlideoutExperience();
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      if (selectedResultRef.current !== submittedSelection) return;
      setLeadSubmissionStatus(getSubmissionErrorStatus(error));
    }
  }, [
    selectedResult,
    checkForAdditionalQuestionsAndSubmit,
    goToSlideoutAdditionalQuestions,
    goToSlideoutExitStrategy,
    resetSlideoutExperience,
    trackSubmittedResult,
    shouldShowThankYou,
  ]);

  const onAdditionalQuestionsSubmit = useCallback(
    async (formData: Record<string, string | number>) => {
      const submittedSelection = selectedResult;
      try {
        setLeadSubmissionStatus(SubmissionStatus.SUBMITTING);
        await submitWithAdditionalData(formData);
        trackSubmittedResult(submittedSelection);
        if (selectedResultRef.current !== submittedSelection) return;
        setLeadSubmissionStatus(SubmissionStatus.SUBMITTED);
        await new Promise((resolve) => setTimeout(resolve, 1500));
        if (selectedResultRef.current !== submittedSelection) return;
        setLeadSubmissionStatus(undefined);
        if (!shouldShowThankYou) {
          resetSlideoutExperience();
          if (showPopupExperience) goToPopupProgramSelect();
          return;
        }
        if (showSlideoutExperience) goToSlideoutExitStrategy();
        if (showPopupExperience) goToPopupExitStrategy();
      } catch (error) {
        console.error('Error submitting lead with additional data:', error);
        if (selectedResultRef.current !== submittedSelection) return;
        setLeadSubmissionStatus(getSubmissionErrorStatus(error));
      }
    },
    [
      submitWithAdditionalData,
      showSlideoutExperience,
      showPopupExperience,
      goToSlideoutExitStrategy,
      goToPopupExitStrategy,
      goToPopupProgramSelect,
      resetSlideoutExperience,
      trackSubmittedResult,
      selectedResult,
      shouldShowThankYou,
    ],
  );

  // Eventing
  const { correlationId } = useDynamicExperienceContext();
  useEffect(() => {
    if (correlationId) {
      triggerElementLoadedEvent({
        correlationId,
        elementType: EventingLocation.PAGE,
        name: EventingLocation.PAGE,
        location: EventingLocation.PAGE,
      });
      triggerElementViewedEvent({
        correlationId,
        elementType: EventingLocation.PAGE,
        name: EventingLocation.PAGE,
        location: EventingLocation.PAGE,
      });
    }
  }, []);

  const handleViewNextSteps = useCallback(
    (submission: SubmittedProgram) => {
      const allResults = [
        ...recommendedResults,
        ...exactResults,
        ...relatedResults,
        ...spotlightResults,
      ];
      const result = allResults.find(
        (r) => r.programId === submission.program.id,
      );
      if (result) {
        if (!shouldShowThankYou) return;
        selectResult(result);
        resetSubmissionStatusForExitStrategy();
        openSlideoutExperience();
        goToSlideoutExitStrategy();
      }
    },
    [
      recommendedResults,
      exactResults,
      relatedResults,
      spotlightResults,
      selectResult,
      resetSubmissionStatusForExitStrategy,
      openSlideoutExperience,
      goToSlideoutExitStrategy,
      shouldShowThankYou,
    ],
  );

  const handleNextStepsClick = useCallback(
    (result: Result) => {
      if (!shouldShowThankYou) return;
      selectResult(result);
      resetSubmissionStatusForExitStrategy();
      openSlideoutExperience();
      goToSlideoutExitStrategy();
    },
    [
      selectResult,
      resetSubmissionStatusForExitStrategy,
      openSlideoutExperience,
      goToSlideoutExitStrategy,
      shouldShowThankYou,
    ],
  );

  const closeDuplicateSubmissionPopup = useCallback(() => {
    setShowDuplicatePopup(false);
    setDuplicateResult(null);
  }, []);

  const handleDuplicateViewNextSteps = useCallback(() => {
    if (!duplicateResult) {
      closeDuplicateSubmissionPopup();
      return;
    }

    const submittedResult = submittedResults.find(
      (submission) => submission.schoolId === duplicateResult.schoolId,
    );
    const submittedProgramId = submittedResult?.programId;

    const allResults = [
      ...recommendedResults,
      ...exactResults,
      ...relatedResults,
      ...spotlightResults,
    ];
    const result = allResults.find(
      (candidate) => candidate.programId === submittedProgramId,
    );

    closeDuplicateSubmissionPopup();

    if (result) {
      handleNextStepsClick(result);
    }
  }, [
    closeDuplicateSubmissionPopup,
    duplicateResult,
    exactResults,
    handleNextStepsClick,
    recommendedResults,
    relatedResults,
    spotlightResults,
    submittedResults,
  ]);

  // Assemble props
  const singleSubmissionSlideoutProps: SingleSubmissionSlideoutProps = {
    showSlideoutExperience,
    closeSlideoutExperience,
    openProgramSlideoutExperience,
    onNextStepsClick: handleNextStepsClick,
    singleSubmissionExperienceStep,
    onSlideoutProgramSubmit,
    goToSlideoutPiiConfirmation,
    goToSlideoutExitStrategy,
  };

  const popupExperienceProps: PopupExperienceProps = {
    popupExperienceStep,
    onPopupProgramClick,
    showRecommender,
    showSpotlight,
    showPopupExperience,
    closePopupExperience,
    onPopupProgramSubmit,
    goToPopupProgramSelect,
  };

  const resultsProps: ResultsProps = {
    resultsAreLoading,
    exactResults,
    relatedResults,
    recommendedResults,
    spotlightResults,
    selectedResult,
  };

  const leadSubmissionProps: LeadSubmissionProps = {
    canSubmitLead,
    isHeclidLoading: false,
    leadSubmissionStatus,
    setLeadSubmissionStatus,
  };

  const piiProps: PiiProps = {
    subject,
    category,
    firstName: piiFirstName,
    lastName: piiLastName,
    email: piiEmail,
    phone: piiPhone,
    setFirstName: setPiiFirstName,
    setLastName: setPiiLastName,
    setEmail: setPiiEmail,
    setPhone: setPiiPhone,
    setFormErrors: () => {},
    formErrors: [],
    setEditMode,
    editMode,
    piiLeadErrors: [],
    setPiiLeadErrors: () => {},
  };

  const inputProps: InputProps = {
    startTime: parseInt(flowAnswers?.startDate),
  };

  const additionalQuestionsProps: AdditionalQuestionsProps = {
    showAdditionalQuestions,
    additionalQuestions,
    isLoadingQuestions,
    onAdditionalQuestionsSubmit,
    lead,
  };

  const submissionsProps: SubmissionsProps = {
    submittedPrograms,
    submittedResults,
    onViewNextSteps: handleViewNextSteps,
  };

  const props: ResultsPageProps = {
    ...singleSubmissionSlideoutProps,
    ...popupExperienceProps,
    ...resultsProps,
    ...piiProps,
    ...inputProps,
    ...leadSubmissionProps,
    ...additionalQuestionsProps,
    ...submissionsProps,
    partnerIdentifiers,
    title,
    subtitle,
  };

  return (
    <>
      <ResultsPageUi {...props} />
      {showDuplicatePopup && (
        <DuplicateSubmissionPopup
          result={duplicateResult}
          otherPrograms={duplicatePrograms}
          isLoading={duplicateProgramsLoading}
          onClose={closeDuplicateSubmissionPopup}
          onViewNextSteps={handleDuplicateViewNextSteps}
        />
      )}
    </>
  );
};

export default ResultsPage;
